import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function Modal({ open, onOpenChange, title, children, onSubmit, submitLabel = "Save", submitting, testid }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-border max-h-[90vh] overflow-y-auto rounded-2xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle className="font-head tracking-tight text-xl">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title} dialog</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">{children}</div>
        {onSubmit && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="border border-border">Cancel</Button>
            <Button onClick={onSubmit} disabled={submitting} data-testid="modal-submit"
              className="bg-gold text-black hover:brightness-95 font-semibold">
              {submitting ? "Saving…" : submitLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function Field({ label, value, onChange, type = "text", placeholder, testid, required }) {
  const cls = "mt-1 bg-secondary border-transparent focus-visible:ring-primary focus-visible:bg-white";
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}{required && " *"}</Label>
      {type === "password"
        ? <PasswordInput value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={cls} />
        : <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={cls} />}
    </div>
  );
}

export function TextField({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testid} className="mt-1 bg-secondary border-transparent focus-visible:ring-primary focus-visible:bg-white" rows={3} />
    </div>
  );
}

export function Select({ label, value, onChange, options, testid, allowEmpty, emptyLabel = "All" }) {
  return (
    <div>
      {label && <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>}
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1 w-full h-10 rounded-lg bg-secondary border border-transparent px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:bg-white">
        {allowEmpty && <option value="">{emptyLabel}</option>}
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lbl = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{lbl}</option>;
        })}
      </select>
    </div>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none py-1.5" data-testid={testid}>
      <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-muted-foreground/30"}`}>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      <input type="checkbox" className="sr-only" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

export function MultiSelect({ label, options, value = [], onChange, allowCustom, testid }) {
  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter((v) => v !== opt));
    else onChange([...value, opt]);
  };
  const customs = value.filter((v) => !options.includes(v));
  const addCustom = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      e.preventDefault();
      const v = e.target.value.trim();
      if (!value.includes(v)) onChange([...value, v]);
      e.target.value = "";
    }
  };
  return (
    <div data-testid={testid}>
      {label && <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>}
      <div className="mt-1 flex flex-wrap gap-2">
        {options.map((opt) => (
          <button type="button" key={opt} onClick={() => toggle(opt)}
            data-testid={testid ? `${testid}-opt-${opt.replace(/\s+/g, '-').toLowerCase()}` : undefined}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value.includes(opt) ? "bg-primary text-white border-primary" : "bg-secondary border-transparent hover:border-primary/40"}`}>
            {opt}
          </button>
        ))}
        {customs.map((c) => (
          <button type="button" key={c} onClick={() => toggle(c)}
            className="px-3 py-1.5 rounded-full text-xs border bg-primary text-white border-primary">
            {c} ✕
          </button>
        ))}
      </div>
      {allowCustom && (
        <Input onKeyDown={addCustom} placeholder="Type & press Enter to add other…"
          data-testid={testid ? `${testid}-custom` : undefined}
          className="mt-2 bg-secondary border-transparent focus-visible:ring-primary focus-visible:bg-white text-sm" />
      )}
    </div>
  );
}
